import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  ChevronLeft, Sparkles, ArrowRight, BookOpenCheck, Eye, Loader2,
  Volume2, VolumeX, Pause, Play, Square, Settings, Bus, WifiOff, Smartphone, Lightbulb,
} from "lucide-react";
import AudioSettingsDialog from "@/components/AudioSettingsDialog";
import AccessLimitReachedCard from "@/components/AccessLimitReachedCard";
import ExitConfirmDialog from "@/components/ExitConfirmDialog";
import { useExitGuard } from "@/hooks/useExitGuard";
import {
  isTTSSupported, loadVoices, speak, pauseSpeech, resumeSpeech, cancelSpeech, rateFor, delay,
} from "@/lib/tts";
import {
  isMediaSessionSupported, setMediaMetadata, setPlaybackState, setMediaHandlers,
  clearMediaSession,
} from "@/lib/mediaSession";
import { useWakeLock } from "@/lib/wakeLock";
import { useCommuteMode, applyCommuteOverrides } from "@/hooks/useCommuteMode";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";
import { toast } from "sonner";

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const DEFAULT_AUDIO = {
  read_aloud_enabled: false,
  reading_flow: "manual_next",
  delay_before_answer_seconds: 5,
  delay_before_next_question_seconds: 5,
  reading_speed: "normal",
  selected_voice: "default",
};

const PHASE_LABELS = {
  idle: "Idle",
  reading_question: "Reading question…",
  waiting_before_answer: "Waiting before answer…",
  reading_answer: "Reading answer…",
  waiting_before_next: "Waiting before next…",
  paused: "Paused",
  stopped: "Stopped",
  completed: "Completed",
};

export default function StudentReviewMode() {
  const { moduleId } = useParams();
  const [questions, setQuestions] = useState([]);
  const [sessionKey, setSessionKey] = useState(0);
  const [idx, setIdx] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiExp, setAiExp] = useState(null);            // lazily-fetched per current Q
  const aiCache = useRef({});                          // qid → explanation (session-only)

  const openAiDialog = async () => {
    if (!current) return;
    setAiOpen(true);
    if (!current.has_ai_explanation) { setAiExp(null); return; }
    if (aiCache.current[current.id]) { setAiExp(aiCache.current[current.id]); return; }
    setAiLoading(true);
    try {
      const r = await api.get(`/questions/${current.id}/ai-explanation`);
      aiCache.current[current.id] = r.data;
      setAiExp(r.data);
    } catch { setAiExp(null); }
    finally { setAiLoading(false); }
  };
  const [done, setDone] = useState(false);

  // FSRS self-rating ("How was that?") — Review Mode flashcard rating sent to
  // /student/review-mode/rate. `rated` flips true while the POST is in-flight
  // so the buttons disable themselves and don't double-submit.
  const [rating, setRating] = useState(null);  // last picked label (visual only)
  const [rateSubmitting, setRateSubmitting] = useState(false);

  // Audio
  const [settings, setSettings] = useState(DEFAULT_AUDIO);
  const [audioOpen, setAudioOpen] = useState(false);
  const [phase, setPhase] = useState("idle");
  const supported = isTTSSupported();
  const commute = useCommuteMode();
  // Effective settings used by the read chain — commute mode overrides flow/speed/delays
  const effective = commute.active ? applyCommuteOverrides(settings) : settings;
  // Keep the screen awake while Read Aloud is active (prevents Android from
  // pausing SpeechSynthesis when the screen sleeps mid-commute).
  const wakeLock = useWakeLock(effective.read_aloud_enabled && supported);

  // Refs to manage cancellable async flow
  const runIdRef = useRef(0);
  const delayRef = useRef(null);
  const voicesRef = useRef([]);

  // Track latest settings so the unmount cleanup below can decide whether
  // to auto-disable "Enable Read Aloud Mode" on exit — and, if so, send the
  // FULL settings object (not just the flag) so other fields aren't reset
  // to backend defaults.
  const settingsRef = useRef(settings);
  useEffect(() => { settingsRef.current = settings; }, [settings]);

  // Same idea for Commute Mode: if the student turned it explicitly ON,
  // reset it back to "off" when they leave Review Mode so a stale, forgotten
  // toggle doesn't auto-read next time they open a module.
  const commuteRef = useRef(commute);
  useEffect(() => { commuteRef.current = commute; }, [commute]);

  // Fetch questions + access state in parallel so we can render a proper
  // "limit reached" lock card when the backend clips the list to 0.
  const [accessState, setAccessState] = useState(null);

  // Exit confirmation guard — active during an in-progress review session
  const inProgress = !done && questions.length > 0;
  const { confirmOpen, confirmExit, cancelExit } = useExitGuard(inProgress);
  useEffect(() => {
    api.get(`/student/review-questions?module_id=${moduleId}`).then((r) => {
      const filtered = (r.data || []).filter((q) => q.choice_a && q.choice_b);
      setQuestions(shuffle(filtered));
    });
    api.get("/student/access-status").then((r) => setAccessState(r.data)).catch(() => {});
  }, [moduleId, sessionKey]);

  // Fetch audio settings once
  useEffect(() => {
    api.get("/student/audio-settings")
      .then((r) => setSettings({ ...DEFAULT_AUDIO, ...r.data }))
      .catch(() => { /* defaults are fine */ });
    if (supported) loadVoices().then((vs) => { voicesRef.current = vs || []; });
  }, [supported]);

  // Stop speech + delays on unmount / module change / logout (logout removes token → redirect → unmount).
  // ALSO auto-disables "Enable Read Aloud Mode" so the next Review session
  // doesn't start reading automatically without the student opting in again.
  useEffect(() => {
    return () => {
      runIdRef.current += 1;  // invalidate any in-flight chain
      cancelSpeech();
      if (delayRef.current) delayRef.current.cancel();
      clearMediaSession();
      if (settingsRef.current?.read_aloud_enabled) {
        // Fire-and-forget — never block the unmount on a network call.
        // Send the full current settings so voice / speed / delays are
        // preserved; only flip the enable flag off.
        const s = settingsRef.current;
        api.put("/student/audio-settings", {
          read_aloud_enabled: false,
          reading_flow: s.reading_flow || "manual_next",
          delay_before_answer_seconds: Number(s.delay_before_answer_seconds ?? 5),
          delay_before_next_question_seconds: Number(s.delay_before_next_question_seconds ?? 5),
          reading_speed: s.reading_speed || "normal",
          selected_voice: s.selected_voice || "default",
        }).catch(() => {});
      }
      // Reset Commute Mode to "off" if the student had explicitly toggled it
      // ON. Auto-detection ("auto") self-corrects based on device state, so
      // we leave that alone — otherwise landscape mobile users would have to
      // opt back into auto every session.
      if (commuteRef.current?.mode === "on") {
        try { commuteRef.current.setMode("off"); } catch { /* noop */ }
        // Belt-and-suspenders: write directly to localStorage in case the
        // setMode call is racing with unmount and never lands in React state.
        try { localStorage.setItem("pm_commute_mode", "off"); } catch { /* noop */ }
      }
    };
  }, []);
  useEffect(() => {
    // Stop audio when module changes
    runIdRef.current += 1;
    cancelSpeech();
    if (delayRef.current) delayRef.current.cancel();
    clearMediaSession();
  }, [moduleId]);

  const current = questions[idx];
  const correctText = current ? current[`choice_${(current.correct_answer || "").toLowerCase()}`] : "";

  // Resolve selected voice — if user picked "Auto", we return null and let
  // tts.js auto-pick the highest-quality voice on the current device.
  const findVoice = useCallback(() => {
    if (!settings.selected_voice || settings.selected_voice === "default") return null;
    return voicesRef.current.find((v) => v.name === settings.selected_voice) || null;
  }, [settings.selected_voice]);

  const speakOpts = useCallback(() => ({
    rate: rateFor(effective.reading_speed),
    voice: findVoice(),
  }), [effective.reading_speed, findVoice]);

  // Cancel any in-flight async chain and clear local audio state
  const stopAll = useCallback(() => {
    runIdRef.current += 1;
    cancelSpeech();
    if (delayRef.current) { delayRef.current.cancel(); delayRef.current = null; }
    setPhase("stopped");
  }, []);

  // Cancellable delay wrapper
  const wait = (seconds) => {
    if (delayRef.current) delayRef.current.cancel();
    const d = delay(seconds * 1000);
    delayRef.current = d;
    return d.promise;
  };

  // Run the read-aloud chain for the current question. Each invocation gets its own
  // runId — if a newer run starts (Next clicked, settings changed), the old chain
  // exits silently at the next checkpoint.
  const runReadChain = useCallback(async (qIndex) => {
    if (!supported || !effective.read_aloud_enabled) return;
    const q = questions[qIndex];
    if (!q) return;
    const myRun = ++runIdRef.current;
    const isCurrent = () => runIdRef.current === myRun;

    try {
      setPhase("reading_question");
      await speak(q.question_text || "", speakOpts());
      if (!isCurrent()) return;

      setPhase("waiting_before_answer");
      await wait(effective.delay_before_answer_seconds);
      if (!isCurrent()) return;

      setRevealed(true);
      setPhase("reading_answer");
      // Count this question for quota (idempotent on backend) — same as
      // manual showAnswer() click. Without this, audio-reviewed questions
      // wouldn't decrement the per-course counter either.
      api.post("/student/quota/mark-used", { question_id: q.id })
        .then((r) => {
          const newState = r.data?.access_state;
          if (newState) {
            if (
              newState.is_limited &&
              newState.questions_remaining === 1 &&
              accessState?.questions_remaining !== 1
            ) {
              toast.warning("Last free question — upgrade to keep going.", {
                duration: 6000,
                description: "Pay any time from your dashboard to unlock full access.",
              });
            }
            setAccessState(newState);
          }
        })
        .catch(() => {});
      await speak(`Correct answer. ${q[`choice_${(q.correct_answer || "").toLowerCase()}`] || ""}`, speakOpts());
      if (!isCurrent()) return;

      // Auto-advance ONLY when we're in Commute Mode. If the student
      // triggered this chain via the manual Speaker button (commute off),
      // we should NOT hop to the next question — that's what Commute Mode
      // is for. This decouples "read this question aloud once" from
      // "read the whole session hands-free" per the simplified UX
      // (2026-09).
      if (effective.reading_flow === "continuous" && commute.active) {
        setPhase("waiting_before_next");
        await wait(effective.delay_before_next_question_seconds);
        if (!isCurrent()) return;
        // Move forward — DON'T also schedule a runReadChain here. The
        // `[idx, ...]` useEffect below owns the "auto-start on new
        // question" trigger. Firing both created a race where two chains
        // reassigned `audio.src` back-to-back, so the student heard
        // "a few words then the whole question re-read from the top."
        if (qIndex + 1 >= questions.length) {
          setDone(true);
          setPhase("completed");
        } else {
          setIdx(qIndex + 1);
          setRevealed(false);
          setAiOpen(false);
        }
      } else {
        // manual_next: stop and wait
        setPhase("stopped");
      }
    } catch (e) {
      if (e?.message === "delay_cancelled") return; // expected when Next/Stop hit
      // Surface the real reason so we (and students) can see WHY audio
      // failed. Previously errors were swallowed → users only saw "no
      // sound" with zero diagnostic info.
      const msg = e?.message || e?.error || String(e || "unknown");
      console.error("[ReadAloud] chain failed:", msg, e);
      toast.error(`Read Aloud: ${msg}`, { duration: 8000 });
      setPhase("stopped");
    }
  }, [supported, effective, questions, speakOpts, commute.active]);

  // Auto-start chain when question changes AND read-aloud is enabled.
  // Guard: only auto-start when entering a new question (idx changes OR session restart),
  // not when settings toggles (user can manually press Read Question for that).
  useEffect(() => {
    if (!current) return;
    if (effective.read_aloud_enabled && supported) {
      runReadChain(idx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, sessionKey, effective.read_aloud_enabled, commute.active, supported, questions.length]);

  const next = () => {
    stopAll();
    if (idx + 1 >= questions.length) {
      setDone(true);
      setPhase("completed");
      return;
    }
    setIdx((i) => i + 1);
    setRevealed(false);
    setAiOpen(false);
    setRating(null);
    setPhase("idle");
    // useEffect above will trigger runReadChain
  };

  const showAnswer = () => {
    setRevealed(true);
    // Count this question against the per-course limited-access quota
    // (Review Mode never hits /student/answer, so we must call the dedicated
    // quota-mark endpoint to keep the dashboard counter in sync). Idempotent —
    // safe to call repeatedly.
    const q = questions[idx];
    if (q?.id) {
      api.post("/student/quota/mark-used", { question_id: q.id })
        .then((r) => {
          const newState = r.data?.access_state;
          if (newState) {
            // Fire a one-time "last free question" toast when the student has
            // exactly 1 left — highest pay-intent moment.
            if (
              newState.is_limited &&
              newState.questions_remaining === 1 &&
              accessState?.questions_remaining !== 1
            ) {
              toast.warning("Last free question — upgrade to keep going.", {
                duration: 6000,
                description: "Pay any time from your dashboard to unlock full access.",
              });
            }
            setAccessState(newState);
          }
        })
        .catch(() => { /* full-access students / no quota — silent no-op */ });
    }
    if (supported && effective.read_aloud_enabled) {
      // Manual reveal — speak the answer immediately
      stopAll();
      const myRun = ++runIdRef.current;
      setPhase("reading_answer");
      speak(`Correct answer. ${correctText}`, speakOpts())
        .then(() => { if (runIdRef.current === myRun) setPhase("stopped"); })
        .catch(() => { if (runIdRef.current === myRun) setPhase("stopped"); });
    }
  };

  const restart = () => {
    stopAll();
    setIdx(0);
    setRevealed(false);
    setAiOpen(false);
    setDone(false);
    setRating(null);
    setPhase("idle");
    setSessionKey((k) => k + 1);
  };

  // FSRS rating handler — Review Mode "How was that?" buttons.
  // Sends the user's self-rating to the backend, which feeds it into the
  // FSRS scheduler so the next review date adapts to perceived difficulty.
  // Auto-advances to the next card (or finishes the session on the last).
  const rateAndAdvance = async (label) => {
    if (rateSubmitting) return;
    const q = questions[idx];
    if (!q?.id) return;
    setRating(label);
    setRateSubmitting(true);
    try {
      await api.post("/student/review-mode/rate", { question_id: q.id, rating: label });
    } catch (e) {
      // Non-blocking — still advance even if scheduler call fails; we toast a
      // soft warning so the student knows their rating wasn't recorded.
      toast.warning("Couldn't record rating — review continues", { duration: 2500 });
    } finally {
      setRateSubmitting(false);
      next();
    }
  };

  // --- MediaSession: lock-screen + hardware media key integration ---
  // Update metadata whenever the current question or position changes.
  useEffect(() => {
    if (!isMediaSessionSupported() || !current) return;
    setMediaMetadata({
      title: (current.question_text || "Review question").slice(0, 140),
      artist: `PrepMasters · Question ${idx + 1} of ${questions.length}`,
      album: "Read Aloud Mode",
    });
  }, [current, idx, questions.length]);

  // Reflect phase → playbackState
  useEffect(() => {
    if (!isMediaSessionSupported()) return;
    if (phase === "paused") setPlaybackState("paused");
    else if (["reading_question", "waiting_before_answer", "reading_answer", "waiting_before_next"].includes(phase)) {
      setPlaybackState("playing");
    } else setPlaybackState("none");
  }, [phase]);

  // Read-aloud focus is now claimed by the real HTML5 <audio> element in
  // tts.js, so no silent-loop workaround is needed. MediaSession metadata
  // and handlers (set below) are attached directly to that playback.

  // Audio controls
  //
  // Speaker button (manual one-shot): reads the CURRENT question + answer once.
  // Works even when Commute Mode is off — we temporarily flip settings.read_aloud_enabled
  // to true for the duration of the chain so runReadChain doesn't bail at its
  // early-exit gate. In manual mode we don't auto-advance to the next
  // question — that only happens in Commute Mode.
  const onReadQuestion = () => {
    if (!supported) return toast.error("Read aloud not supported on this device");
    if (!settings.read_aloud_enabled && !commute.active) {
      // Temporarily enable read-aloud in memory so runReadChain proceeds.
      // NOT persisted — next session starts with read-aloud off.
      setSettings((s) => ({ ...s, read_aloud_enabled: true }));
      // Give React one tick to commit the state change before firing the chain.
      setTimeout(() => runReadChain(idx), 0);
      return;
    }
    runReadChain(idx);
  };
  const onPause = () => { pauseSpeech(); setPhase("paused"); };
  const onResume = () => { resumeSpeech(); setPhase((p) => (p === "paused" ? "reading_question" : p)); };
  const onStop = () => stopAll();

  // Bind MediaSession action handlers to the same controls the on-screen buttons use.
  // Re-bound on every render so handlers always close over the latest state.
  useEffect(() => {
    if (!isMediaSessionSupported()) return undefined;
    if (!effective.read_aloud_enabled) {
      setMediaHandlers({});
      return undefined;
    }
    setMediaHandlers({
      play: () => { resumeSpeech(); setPhase((p) => (p === "paused" ? "reading_question" : p)); setPlaybackState("playing"); },
      pause: () => { pauseSpeech(); setPhase("paused"); setPlaybackState("paused"); },
      stop: () => stopAll(),
      nexttrack: () => next(),
      previoustrack: () => { stopAll(); runReadChain(idx); },  // re-read current question
      seekforward: () => next(),
      seekbackward: () => { stopAll(); runReadChain(idx); },
    });
    return () => setMediaHandlers({});
  }, [effective.read_aloud_enabled, idx, runReadChain, stopAll]);  // eslint-disable-line react-hooks/exhaustive-deps

  const renderText = (text, keyword) => {
    if (!keyword || !text) return text;
    if (!text.toLowerCase().includes(keyword.toLowerCase())) return text;
    const i = text.toLowerCase().indexOf(keyword.toLowerCase());
    return (<>{text.slice(0, i)}<span className="keyword-highlight">{text.slice(i, i + keyword.length)}</span>{text.slice(i + keyword.length)}</>);
  };

  if (questions.length === 0) {
    const limitReached = accessState && !accessState.is_full && accessState.questions_remaining === 0;
    if (limitReached) {
      return (
        <div className="space-y-6 max-w-2xl mx-auto">
          <Link to="/student/courses" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ChevronLeft className="w-4 h-4" />Back</Link>
          <AccessLimitReachedCard accessState={accessState} />
        </div>
      );
    }
    return (
      <div className="space-y-6">
        <Link to="/student/courses" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ChevronLeft className="w-4 h-4" />Back</Link>
        <Card className="p-10 text-center text-slate-500">No questions in this module yet.</Card>
      </div>
    );
  }

  if (done) {
    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="review-mode-complete">
        <Card className="p-10 border-slate-200 text-center">
          <div className="mx-auto h-14 w-14 rounded-full bg-blue-100 grid place-items-center text-blue-700 mb-4">
            <BookOpenCheck className="w-7 h-7" strokeWidth={1.5} />
          </div>
          <h2 className="font-display text-3xl font-bold">Review Complete</h2>
          <p className="text-slate-600 mt-2">
            {settings.read_aloud_enabled
              ? "Review completed. Great job studying with Audio Study Mode!"
              : <>You have reviewed <span className="font-semibold text-slate-900">{questions.length}</span> question(s) in this module.</>}
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Button variant="outline" onClick={restart} className="border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid="restart-review-btn">Restart Review</Button>
            <Link to="/student/courses"><Button className="bg-[#1E3A8A] hover:bg-[#1E40AF] w-full sm:w-auto" data-testid="back-to-courses-btn">Back to Module</Button></Link>
          </div>
        </Card>
      </div>
    );
  }

  if (!current) return null;

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="student-review-mode">
      <Link to="/student/courses" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" onClick={stopAll}>
        <ChevronLeft className="w-4 h-4" />Exit Review
      </Link>

      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600 flex items-center gap-2">
            <BookOpenCheck className="w-3.5 h-3.5" /> Review Mode
          </div>
          <div className="text-sm text-slate-500 mt-1">Question {idx + 1} of {questions.length}</div>
        </div>
        {accessState && accessState.is_limited && accessState.question_limit > 0 && (
          <div
            className="px-3 py-1.5 rounded-full bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 text-amber-900 dark:text-amber-100 text-xs font-semibold"
            data-testid="review-mode-quota-pill"
          >
            Temporary access · {accessState.questions_remaining} of {accessState.question_limit} left
          </div>
        )}
      </div>
      <Progress value={(idx / questions.length) * 100} className="h-2" />

      {/* AUDIO STUDY BAR */}
      <Card
        className={`p-3 border-slate-200 bg-gradient-to-r ${commute.active ? "from-amber-50 to-orange-50 border-amber-200" : settings.read_aloud_enabled ? "from-indigo-50 to-violet-50 border-indigo-200" : "from-slate-50 to-slate-50"}`}
        data-testid="audio-bar"
      >
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 min-w-0 flex-wrap">
            {/* Read Aloud pill toggle removed 2026-09 per user request —
                the workflow is now: click the speaker icon on the right
                to read the CURRENT question once, or turn on Commute Mode
                (below) for hands-free continuous reading. This kept the
                audio bar to two intent-driven controls instead of three
                overlapping ones. */}

            {/* Commute Mode badge / popover */}
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={`h-9 px-3 rounded-full inline-flex items-center gap-1.5 text-xs font-bold transition-colors ${commute.active ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-sm shadow-amber-300" : "bg-white border border-slate-200 text-slate-700"}`}
                  data-testid="commute-mode-btn"
                  aria-pressed={commute.active}
                  title="Hands-free Commute Mode"
                >
                  <Bus className="w-4 h-4" />
                  <span>Commute Mode{commute.active ? ": On" : ""}</span>
                  {commute.auto && <span className="text-[9px] uppercase tracking-wider opacity-90">Auto</span>}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-3" align="start" data-testid="commute-popover">
                <div className="font-display font-bold text-sm flex items-center gap-1.5">
                  <Bus className="w-4 h-4 text-amber-600" /> Hands-free Commute Mode
                </div>
                <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                  Auto-switches to continuous flow, slow speed, and 3-second delays — perfect for podcast-style listening on your commute.
                </p>
                {commute.reasons.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {commute.reasons.includes("offline") && (
                      <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-[10px]">
                        <WifiOff className="w-3 h-3 mr-0.5" /> Offline
                      </Badge>
                    )}
                    {commute.reasons.includes("landscape") && (
                      <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-[10px]">
                        <Smartphone className="w-3 h-3 mr-0.5 rotate-90" /> Landscape
                      </Badge>
                    )}
                  </div>
                )}
                <div className="mt-3 space-y-1">
                  {[
                    { v: "auto", label: "Auto (offline or landscape)" },
                    { v: "on", label: "Always on" },
                    { v: "off", label: "Off" },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => commute.setMode(opt.v)}
                      className={`commute-option ${commute.mode === opt.v ? "is-selected" : ""}`}
                      data-testid={`commute-mode-${opt.v}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {commute.active && (
                  <div className="mt-3 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
                    Now: continuous · slow · 3s delays
                  </div>
                )}
              </PopoverContent>
            </Popover>

            {effective.read_aloud_enabled && (
              <Badge variant="outline" className="bg-white/70 text-[10px] font-bold uppercase tracking-wider" data-testid="audio-phase">
                {PHASE_LABELS[phase] || phase}
              </Badge>
            )}
            {effective.read_aloud_enabled && wakeLock.locked && (
              <Badge
                variant="outline"
                className="bg-emerald-50 text-emerald-800 border-emerald-200 text-[10px] font-semibold inline-flex items-center gap-1"
                title="Screen kept awake while Read Aloud is playing"
                data-testid="wakelock-indicator"
              >
                <Lightbulb className="w-3 h-3" /> Screen awake
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline" size="sm"
              className="h-9 px-3 border-indigo-300 text-indigo-700 hover:bg-indigo-50 gap-1.5"
              onClick={onReadQuestion}
              disabled={!supported}
              title="Read the current question and answer aloud once"
              data-testid="audio-read-btn"
            >
              <Volume2 className="w-4 h-4" />
              <span className="text-xs font-bold hidden sm:inline">Read this Q</span>
            </Button>
            <Button
              variant="ghost" size="icon"
              className="h-9 w-9 text-indigo-700 hover:bg-indigo-100"
              onClick={phase === "paused" ? onResume : onPause}
              disabled={!supported}
              title={phase === "paused" ? "Resume" : "Pause"}
              data-testid="audio-pause-btn"
            >
              {phase === "paused" ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost" size="icon"
              className="h-9 w-9 text-rose-600 hover:bg-rose-50"
              onClick={onStop}
              disabled={!supported}
              title="Stop"
              data-testid="audio-stop-btn"
            >
              <Square className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost" size="icon"
              className="h-9 w-9 text-slate-600 hover:bg-slate-100"
              onClick={() => setAudioOpen(true)}
              title="Audio Settings"
              data-testid="audio-settings-btn"
            >
              <Settings className="w-4 h-4" />
            </Button>
          </div>
        </div>
        {!supported && (
          <p className="text-[11px] text-rose-700 mt-2" data-testid="audio-unsupported-banner">
            Read Aloud is not supported on this device or browser.
          </p>
        )}
      </Card>

      <Card className="p-6 sm:p-8 border-slate-200">
        <p className="text-xl leading-relaxed text-slate-900 font-medium">{renderText(current.question_text, current.keyword)}</p>

        {!revealed ? (
          <Button
            className="mt-8 w-full h-11 bg-[#1E3A8A] hover:bg-[#1E40AF]"
            onClick={showAnswer}
            data-testid="show-answer-btn"
          >
            <Eye className="w-4 h-4 mr-2" /> Show Answer
          </Button>
        ) : (
          <>
            <div className="mt-6 p-5 rounded-md border border-green-200 bg-green-50">
              <div className="text-xs font-bold text-green-800 uppercase tracking-wide mb-1">Correct Answer</div>
              <p className="text-base font-semibold text-slate-900">{correctText}</p>
            </div>

            {/* FSRS self-rating — "How was that?" pickers feed the spaced
                repetition scheduler so the next review adapts to perceived
                difficulty. Clicking any button rates the card AND advances. */}
            <div className="mt-6" data-testid="fsrs-rating-row">
              <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-indigo-500" /> How was that?
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Button
                  variant="outline"
                  disabled={rateSubmitting}
                  onClick={() => rateAndAdvance("again")}
                  className="h-11 border-rose-300 text-rose-700 hover:bg-rose-50 hover:text-rose-800 font-semibold disabled:opacity-50"
                  data-testid="rate-again-btn"
                >
                  Again
                </Button>
                <Button
                  variant="outline"
                  disabled={rateSubmitting}
                  onClick={() => rateAndAdvance("hard")}
                  className="h-11 border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800 font-semibold disabled:opacity-50"
                  data-testid="rate-hard-btn"
                >
                  Hard
                </Button>
                <Button
                  variant="outline"
                  disabled={rateSubmitting}
                  onClick={() => rateAndAdvance("good")}
                  className="h-11 border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 font-semibold disabled:opacity-50"
                  data-testid="rate-good-btn"
                >
                  Good
                </Button>
                <Button
                  variant="outline"
                  disabled={rateSubmitting}
                  onClick={() => rateAndAdvance("easy")}
                  className="h-11 border-indigo-300 text-indigo-700 hover:bg-indigo-50 hover:text-indigo-800 font-semibold disabled:opacity-50"
                  data-testid="rate-easy-btn"
                >
                  Easy
                </Button>
              </div>
              <p className="text-[11px] text-slate-500 mt-2">
                Your rating tunes when this card will appear again in Smart Review.
              </p>
            </div>

            {/* Skip without rating — kept for users who just want to flip through */}
            <Button
              variant="ghost"
              className="mt-3 w-full h-10 text-slate-600 hover:bg-slate-100 text-sm"
              onClick={next}
              disabled={rateSubmitting}
              data-testid="next-question-btn"
            >
              {idx + 1 >= questions.length ? "Finish without rating" : "Skip rating · Next"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>

            {/* Rationale (unified): one button that opens ONE dialog showing
                the AI-generated per-choice explanation if available, else
                the imported plain-text rationale as a fallback. Previously
                these were two separate UI elements ("Rationale" popup +
                "Explain by AI" dialog) which confused students. */}
            <Button
              variant="outline"
              className="mt-3 w-full h-11 border-indigo-300 text-indigo-700 hover:bg-indigo-50 hover:text-indigo-800"
              onClick={openAiDialog}
              data-testid="rationale-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" strokeWidth={2} /> Rationale
            </Button>
          </>
        )}
      </Card>

      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-indigo-600" /> Rationale
            </DialogTitle>
            <DialogDescription>Why the correct answer is correct.</DialogDescription>
          </DialogHeader>

          {aiLoading ? (
            <div className="py-8 text-center text-slate-500 flex flex-col items-center gap-2">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
              <p className="text-sm">Loading rationale…</p>
            </div>
          ) : !aiExp ? (
            // No AI-generated rationale exists yet — fall back to the
            // imported plain-text rationale field on the question (from
            // the CSV/Excel column K) so students still see SOMETHING.
            // Skips display when rationale is just a difficulty word
            // (defensive against poisoned imports — see RationaleButton).
            ((() => {
              const rat = (current?.rationale || "").trim();
              const isDifficultyWord = /^(easy|average|difficult|medium|hard)$/i.test(rat);
              return rat && !isDifficultyWord ? (
                <div className="space-y-2 py-2" data-testid="rationale-plain-fallback">
                  <div className="text-xs font-bold text-indigo-700 uppercase tracking-wide">Explanation</div>
                  <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-line">{rat}</p>
                </div>
              ) : (
                <div className="py-8 text-center">
                  <div className="mx-auto h-12 w-12 rounded-full bg-slate-100 grid place-items-center text-slate-400 mb-3">
                    <Sparkles className="w-6 h-6" />
                  </div>
                  <p className="text-sm text-slate-600">No rationale is available for this question yet.</p>
                </div>
              );
            })())
          ) : (
            <div className="space-y-4">
              <div className="ai-card p-4">
                <div className="text-xs font-bold text-indigo-700 uppercase tracking-wide">Correct Answer</div>
                <div className="text-sm font-semibold text-slate-900 mt-1 mb-3">{correctText}</div>
                <div className="text-xs font-bold text-indigo-700 uppercase tracking-wide mb-1">Why this is correct</div>
                <p className="text-sm leading-relaxed text-slate-700">{aiExp.correct_answer_explanation}</p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setAiOpen(false)} data-testid="close-ai-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AudioSettingsDialog
        open={audioOpen}
        onOpenChange={setAudioOpen}
        settings={settings}
        onSaved={(s) => { setSettings({ ...DEFAULT_AUDIO, ...s }); }}
      />

      <ExitConfirmDialog
        open={confirmOpen}
        onCancel={cancelExit}
        onConfirm={confirmExit}
        mode="review"
      />
    </div>
  );
}
